import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './AdminModals.css';

const AdminModals = ({
  // Modal states
  showRejectionModal,
  setShowRejectionModal,
  showChangeLocationModal,
  setShowChangeLocationModal,
  showBulkActionModal,
  setShowBulkActionModal,
  showDelegateModal,
  setShowDelegateModal,
  showReportModal,
  setShowReportModal,
  showAuditLogModal,
  setShowAuditLogModal,
  showSMSModal,
  setShowSMSModal,
  showWhatsAppModal,
  setShowWhatsAppModal,
  showEmailModal,
  setShowEmailModal,
  showScheduleModal,
  setShowScheduleModal,
  showConflictModal,
  setShowConflictModal,
  showWaitlistModal,
  setShowWaitlistModal,
  showFeedbackModal,
  setShowFeedbackModal,
  showMaintenanceModal,
  setShowMaintenanceModal,
  showBlackoutModal,
  setShowBlackoutModal,
  showPricingModal,
  setShowPricingModal,
  showPermissionsModal,
  setShowPermissionsModal,
  showAnnouncementModal,
  setShowAnnouncementModal,

  // Selected items
  selectedBooking,
  selectedMember,
  selectedBookings,
  filteredBookings,

  // Data
  facilities,
  sections,
  equipmentCatalog,
  availableUsers,
  auditLog,
  communicationHistory,
  locationChangeHistory,
  conflicts,
  feedbacks,
  maintenanceSchedule,
  blackoutDates,
  pricingRules,
  permissions,
  announcements,

  // Form states
  rejectionReason,
  setRejectionReason,
  newLocation,
  setNewLocation,
  locationChangeReason,
  setLocationChangeReason,
  availableLocations,
  smsMessage,
  setSmsMessage,
  whatsAppMessage,
  setWhatsAppMessage,
  emailSubject,
  setEmailSubject,
  emailBody,
  setEmailBody,
  maintenanceItem,
  setMaintenanceItem,
  blackoutItem,
  setBlackoutItem,
  pricingItem,
  setPricingItem,
  selectedRole,
  setSelectedRole,
  rolePermissions,
  setRolePermissions,
  announcementItem,
  setAnnouncementItem,

  // Actions
  handleAdminAction,
  handleMemberAction,
  handleBulkAction,
  handleBulkCommunication,
  handleDelegate,
  handleLocationChangeSubmit,
  resolveConflict,
  respondToFeedback,
  scheduleMaintenance,
  cancelMaintenance,
  addBlackoutDate,
  removeBlackoutDate,
  addPricingRule,
  updatePricingRule,
  deletePricingRule,
  updateRolePermissions,
  createAnnouncement,
  deleteAnnouncement,
  promoteFromWaitlist,
  removeFromWaitlist,

  // User info
  user,
  canApprove
}) => {
  // Complete list of ALL 19 facility locations
  const ALL_FACILITY_LOCATIONS = [
    // Event Spaces (5)
    { name: 'Abuja Boulevard', category: 'Event Space' },
    { name: 'Banquet Hall', category: 'Event Space' },
    { name: 'Market Square', category: 'Event Space' },
    { name: 'Marquee Tent', category: 'Event Space' },
    { name: 'West Bar Meeting Room', category: 'Meeting Room' },
    
    // Bars/Lounges (4)
    { name: 'Clubhouse West-Bar', category: 'Bar/Lounge' },
    { name: 'DBN Bar', category: 'Bar/Lounge' },
    { name: 'Golf Course Bar', category: 'Bar/Lounge' },
    { name: 'Hippo Bar', category: 'Bar/Lounge' },
    
    // Meeting Spaces (1)
    { name: 'Clubhouse Alcove', category: 'Meeting Room' },
    
    // Sports Facilities (5)
    { name: 'Sports Hall Complex', category: 'Sports' },
    { name: 'Tennis Courts', category: 'Sports' },
    { name: 'Swimming Pool', category: 'Aquatics' },
    { name: 'Football Pitch', category: 'Sports' },
    { name: 'Squash Courts', category: 'Sports' },
    
    // Golf Course (1)
    { name: 'Golf Course', category: 'Sports' },
    
    // Kids Area (1)
    { name: 'Kids Play Area', category: 'Kids' },
    
    // Wellness Center (1)
    { name: 'Wellness Center', category: 'Wellness' },
    
    // Nature Park (1)
    { name: 'Finima Nature Park', category: 'Outdoor' }
  ];

  // Group locations by category for better organization
  const getLocationsByCategory = () => {
    const categorized = {};
    ALL_FACILITY_LOCATIONS.forEach(location => {
      if (!categorized[location.category]) {
        categorized[location.category] = [];
      }
      categorized[location.category].push(location.name);
    });
    return categorized;
  };

  // Get all location names
  const getAllLocationNames = () => {
    return ALL_FACILITY_LOCATIONS.map(loc => loc.name);
  };

  // Get available locations (exclude current location)
  const getAvailableLocationNames = () => {
    const allLocations = getAllLocationNames();
    if (selectedBooking && selectedBooking.location) {
      return allLocations.filter(loc => loc !== selectedBooking.location);
    }
    return allLocations;
  };

  // Format date nicely
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Format time
  const formatTime = (time) => {
    if (!time) return 'N/A';
    if (time.includes(':')) {
      const [hours, minutes] = time.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    }
    return time;
  };

  return (
    <AnimatePresence>
      {/* Rejection Modal */}
      {showRejectionModal && selectedBooking && (
        <motion.div 
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowRejectionModal(false)}
        >
          <motion.div 
            className="modal rejection-modal"
            initial={{ scale: 0.9, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 50 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                <i className="fa-solid fa-times-circle"></i>
                Reject Booking
              </h2>
              <button className="close-btn" onClick={() => setShowRejectionModal(false)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p className="modal-description">
                Please provide a reason for rejection. This will be sent to the user.
              </p>

              <div className="selected-booking-info">
                <p><strong>Booking:</strong> #{selectedBooking.booking_number || selectedBooking.requestId || selectedBooking.id}</p>
                <p><strong>Requestor:</strong> {selectedBooking.member_full_name || selectedBooking.bookedBy || selectedBooking.full_name}</p>
                <p><strong>Date:</strong> {formatDate(selectedBooking.booking_date || selectedBooking.date)}</p>
                <p><strong>Time:</strong> {formatTime(selectedBooking.start_time || selectedBooking.startTime)} - {formatTime(selectedBooking.end_time || selectedBooking.endTime)}</p>
                <p><strong>Location:</strong> {selectedBooking.location || selectedBooking.facility_name}</p>
              </div>

              <textarea
                rows="4"
                placeholder="Enter rejection reason..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="rejection-input"
              />

              <div className="notification-preview">
                <h4>Notifications will be sent via:</h4>
                <ul>
                  <li><i className="fa-solid fa-bell"></i> In-app notification</li>
                  {selectedBooking.contact_phone && (
                    <li><i className="fa-solid fa-sms"></i> SMS to {selectedBooking.contact_phone}</li>
                  )}
                  {selectedBooking.contact_whatsapp && (
                    <li><i className="fa-brands fa-whatsapp"></i> WhatsApp to {selectedBooking.contact_whatsapp}</li>
                  )}
                  {selectedBooking.contact_email && (
                    <li><i className="fa-solid fa-envelope"></i> Email to {selectedBooking.contact_email}</li>
                  )}
                </ul>
              </div>

              <div className="modal-footer">
                <button 
                  className="btn-secondary"
                  onClick={() => setShowRejectionModal(false)}
                >
                  Cancel
                </button>
                <button 
                  className="btn-danger"
                  onClick={() => {
                    handleAdminAction(selectedBooking.id, 'rejected', rejectionReason);
                    setShowRejectionModal(false);
                    setRejectionReason('');
                  }}
                  disabled={!rejectionReason.trim()}
                >
                  <i className="fa-solid fa-times"></i> Reject Booking
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Change Location Modal - WITH ALL 19 FACILITY LOCATIONS */}
      {showChangeLocationModal && selectedBooking && (
        <motion.div 
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowChangeLocationModal(false)}
        >
          <motion.div 
            className="modal location-modal"
            initial={{ scale: 0.9, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 50 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                <i className="fa-solid fa-location-dot"></i>
                Change Location - Booking #{selectedBooking.booking_number || selectedBooking.requestId || selectedBooking.id}
              </h2>
              <button className="close-btn" onClick={() => setShowChangeLocationModal(false)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              {/* Booking Summary Section */}
              <div className="booking-summary-section">
                <h3 className="section-title">
                  <i className="fa-solid fa-receipt"></i>
                  Booking Details
                </h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Booking ID:</label>
                    <span>{selectedBooking.id}</span>
                  </div>
                  <div className="info-item">
                    <label>Booking Number:</label>
                    <span className="booking-number">{selectedBooking.booking_number || selectedBooking.requestId}</span>
                  </div>
                  <div className="info-item">
                    <label>Booking Type:</label>
                    <span className={`type-badge ${selectedBooking.booking_type || selectedBooking.type}`}>
                      {selectedBooking.booking_type || selectedBooking.type || 'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Status:</label>
                    <span className={`status-badge ${selectedBooking.status}`}>
                      {selectedBooking.status?.toUpperCase() || 'PENDING'}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Booking Date:</label>
                    <span className="booking-date">{formatDate(selectedBooking.booking_date || selectedBooking.date)}</span>
                  </div>
                  <div className="info-item">
                    <label>Time:</label>
                    <span className="booking-time">
                      {formatTime(selectedBooking.start_time || selectedBooking.startTime)} - {formatTime(selectedBooking.end_time || selectedBooking.endTime)}
                    </span>
                  </div>
                  {selectedBooking.facility_name && (
                    <div className="info-item">
                      <label>Facility:</label>
                      <span>{selectedBooking.facility_name}</span>
                    </div>
                  )}
                  {selectedBooking.event_type && (
                    <div className="info-item">
                      <label>Event Type:</label>
                      <span>{selectedBooking.event_type}</span>
                    </div>
                  )}
                  {selectedBooking.guests && (
                    <div className="info-item">
                      <label>Guests:</label>
                      <span>{selectedBooking.guests}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Member Details Section */}
              <div className="member-details-section">
                <h3 className="section-title">
                  <i className="fa-solid fa-user"></i>
                  Member Information
                </h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Member Name:</label>
                    <span className="member-name">
                      {selectedBooking.member_full_name || 
                       `${selectedBooking.member_first_name || ''} ${selectedBooking.member_last_name || ''}`.trim() ||
                       selectedBooking.bookedBy ||
                       selectedBooking.full_name ||
                       'N/A'}
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Membership Number:</label>
                    <span>{selectedBooking.member_membership_number || selectedBooking.membership_number || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <label>Email:</label>
                    <span>{selectedBooking.member_email || selectedBooking.userEmail || selectedBooking.email || 'N/A'}</span>
                  </div>
                  <div className="info-item">
                    <label>Phone:</label>
                    <span>{selectedBooking.member_phone || selectedBooking.userPhone || selectedBooking.phone || 'N/A'}</span>
                  </div>
                  {selectedBooking.contact_whatsapp && (
                    <div className="info-item">
                      <label>WhatsApp:</label>
                      <span>{selectedBooking.contact_whatsapp}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Current Location Section */}
              <div className="current-location-section">
                <h3 className="section-title">
                  <i className="fa-solid fa-location-dot"></i>
                  Current Location
                </h3>
                <div className="current-location-box">
                  <i className="fa-solid fa-map-pin"></i>
                  <strong>{selectedBooking.location || selectedBooking.facility_name || 'Not specified'}</strong>
                </div>
              </div>

              {/* Notes Section */}
              {(selectedBooking.notes || selectedBooking.special_requirements) && (
                <div className="notes-section">
                  <h3 className="section-title">
                    <i className="fa-solid fa-note-sticky"></i>
                    Special Requests / Notes
                  </h3>
                  <div className="notes-box">
                    {selectedBooking.notes || selectedBooking.special_requirements}
                  </div>
                </div>
              )}

              {/* Equipment Section */}
              {selectedBooking.equipment_items && selectedBooking.equipment_items.length > 0 && (
                <div className="equipment-section">
                  <h3 className="section-title">
                    <i className="fa-solid fa-tools"></i>
                    Equipment Requested
                  </h3>
                  <div className="equipment-list">
                    {selectedBooking.equipment_items.map((item, idx) => (
                      <span key={idx} className="equipment-tag">
                        {typeof item === 'string' ? item : item.name || item.item}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Jersey Request Section */}
              {selectedBooking.jersey_request && selectedBooking.jersey_request.size && (
                <div className="jersey-section">
                  <h3 className="section-title">
                    <i className="fa-solid fa-shirt"></i>
                    Jersey Request
                  </h3>
                  <div className="jersey-details">
                    <span><strong>Size:</strong> {selectedBooking.jersey_request.size}</span>
                    {selectedBooking.jersey_request.quantity && (
                      <span><strong>Quantity:</strong> {selectedBooking.jersey_request.quantity}</span>
                    )}
                  </div>
                </div>
              )}

              {/* Location Change Form - WITH ALL 19 FACILITY LOCATIONS */}
              <div className="location-change-form">
                <h3 className="section-title">
                  <i className="fa-solid fa-arrows-spin"></i>
                  Change Location
                </h3>
                
                <div className="form-group">
                  <label>Select New Location <span className="required">*</span></label>
                  <select
                    value={newLocation}
                    onChange={(e) => setNewLocation(e.target.value)}
                    className="location-select"
                  >
                    <option value="">-- Choose a facility location --</option>
                    
                    {/* Group locations by category */}
                    {Object.entries(getLocationsByCategory()).map(([category, locations]) => (
                      <optgroup key={category} label={`${category} (${locations.length})`}>
                        {locations.map(location => (
                          <option 
                            key={location} 
                            value={location}
                            disabled={location === selectedBooking.location}
                            style={{
                              color: location === selectedBooking.location ? '#9ca3af' : '#1f2937',
                              backgroundColor: location === selectedBooking.location ? '#f3f4f6' : 'white'
                            }}
                          >
                            {location}
                            {location === selectedBooking.location && ' (Current Location - Cannot Select)'}
                          </option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  
                  {selectedBooking.location && (
                    <div className="form-hint">
                      <i className="fa-solid fa-info-circle"></i>
                      Current location "{selectedBooking.location}" is disabled. Please select a different venue from the list above.
                    </div>
                  )}
                  
                  <div className="location-count-info">
                    <i className="fa-solid fa-building"></i>
                    Total {getAllLocationNames().length} facilities available
                  </div>
                </div>

                <div className="form-group">
                  <label>Reason for Change <span className="required">*</span></label>
                  <textarea
                    rows="4"
                    placeholder="Explain why the location is being changed... This will be sent to the member."
                    value={locationChangeReason}
                    onChange={(e) => setLocationChangeReason(e.target.value)}
                  />
                  <small className="helper-text">
                    <i className="fa-solid fa-info-circle"></i>
                    This reason will be included in the notification sent to the member
                  </small>
                </div>
              </div>

              <div className="modal-footer">
                <button 
                  className="btn-secondary"
                  onClick={() => {
                    setShowChangeLocationModal(false);
                    setNewLocation('');
                    setLocationChangeReason('');
                  }}
                >
                  Cancel
                </button>
                <button 
                  className="btn-primary"
                  onClick={handleLocationChangeSubmit}
                  disabled={!newLocation || !locationChangeReason}
                >
                  <i className="fa-solid fa-check"></i> Confirm Location Change
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Bulk Action Modal */}
      {showBulkActionModal && (
        <motion.div 
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowBulkActionModal(false)}
        >
          <motion.div 
            className="modal bulk-modal"
            initial={{ scale: 0.9, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 50 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                <i className="fa-solid fa-layer-group"></i>
                Bulk Actions
              </h2>
              <button className="close-btn" onClick={() => setShowBulkActionModal(false)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p className="selected-summary">
                Selected <strong>{selectedBookings?.length || 0}</strong> booking(s)
              </p>

              <div className="bulk-actions-grid">
                <button 
                  className="bulk-action-btn approve"
                  onClick={() => handleBulkAction('approved')}
                >
                  <i className="fa-solid fa-check-circle"></i>
                  <span>Approve All</span>
                </button>

                <button 
                  className="bulk-action-btn reject"
                  onClick={() => handleBulkAction('rejected')}
                >
                  <i className="fa-solid fa-times-circle"></i>
                  <span>Reject All</span>
                </button>

                <button 
                  className="bulk-action-btn waitlist"
                  onClick={() => handleBulkAction('waitlist')}
                >
                  <i className="fa-solid fa-clock"></i>
                  <span>Waitlist All</span>
                </button>
              </div>

              {selectedBookings && selectedBookings.length > 0 && (
                <div className="selected-list-preview">
                  <h4>Selected Items:</h4>
                  <div className="preview-items">
                    {filteredBookings
                      ?.filter(b => selectedBookings.includes(b.id))
                      ?.slice(0, 5)
                      .map(b => (
                        <div key={b.id} className="preview-item">
                          <span className="item-id">#{b.booking_number || b.id}</span>
                          <span className="item-name">{b.member_full_name || b.full_name}</span>
                        </div>
                      ))}
                    {selectedBookings.length > 5 && (
                      <div className="more-items">+{selectedBookings.length - 5} more</div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Delegate Modal */}
      {showDelegateModal && (
        <motion.div 
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowDelegateModal(false)}
        >
          <motion.div 
            className="modal delegate-modal"
            initial={{ scale: 0.9, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 50 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                <i className="fa-solid fa-user-plus"></i>
                Delegate Authority
              </h2>
              <button className="close-btn" onClick={() => setShowDelegateModal(false)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <p className="modal-description">
                Select users to delegate approval authority to. Delegation expires in 7 days.
              </p>

              <div className="users-list">
                {availableUsers && availableUsers.map(user => (
                  <div key={user.id} className="user-item">
                    <input
                      type="checkbox"
                      id={`user-${user.id}`}
                      onChange={(e) => {
                        if (e.target.checked) {
                          handleDelegate(user.email, ['approve', 'reject', 'change-location']);
                        }
                      }}
                    />
                    <label htmlFor={`user-${user.id}`}>
                      <div className="user-info">
                        <strong>{user.name}</strong>
                        <span className="user-role">{user.role}</span>
                        <small>{user.email}</small>
                      </div>
                      <div className="user-contacts">
                        <span><i className="fa-solid fa-phone"></i> {user.phone}</span>
                        <span><i className="fa-brands fa-whatsapp"></i> {user.whatsApp}</span>
                      </div>
                    </label>
                  </div>
                ))}
              </div>

              <div className="delegation-info">
                <h4>Delegated Permissions:</h4>
                <ul>
                  <li><i className="fa-solid fa-check-circle text-green"></i> Approve bookings</li>
                  <li><i className="fa-solid fa-check-circle text-green"></i> Reject bookings</li>
                  <li><i className="fa-solid fa-check-circle text-green"></i> Change locations</li>
                </ul>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* SMS Modal */}
      {showSMSModal && (
        <motion.div 
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowSMSModal(false)}
        >
          <motion.div 
            className="modal sms-modal"
            initial={{ scale: 0.9, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 50 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                <i className="fa-solid fa-sms"></i>
                Send SMS ({selectedBookings?.length || 0} recipients)
              </h2>
              <button className="close-btn" onClick={() => setShowSMSModal(false)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <textarea
                rows="4"
                placeholder="Type your SMS message..."
                value={smsMessage}
                onChange={(e) => setSmsMessage(e.target.value)}
                maxLength="160"
                className="sms-input"
              />

              <div className="character-count">
                <span className={smsMessage?.length > 140 ? 'warning' : ''}>
                  {smsMessage?.length || 0}/160 characters
                </span>
              </div>

              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowSMSModal(false)}>
                  Cancel
                </button>
                <button 
                  className="btn-primary"
                  onClick={() => handleBulkCommunication('sms')}
                  disabled={!smsMessage}
                >
                  <i className="fa-solid fa-paper-plane"></i> Send SMS
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* WhatsApp Modal */}
      {showWhatsAppModal && (
        <motion.div 
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowWhatsAppModal(false)}
        >
          <motion.div 
            className="modal whatsapp-modal"
            initial={{ scale: 0.9, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 50 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                <i className="fa-brands fa-whatsapp"></i>
                Send WhatsApp ({selectedBookings?.length || 0} recipients)
              </h2>
              <button className="close-btn" onClick={() => setShowWhatsAppModal(false)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <textarea
                rows="4"
                placeholder="Type your WhatsApp message..."
                value={whatsAppMessage}
                onChange={(e) => setWhatsAppMessage(e.target.value)}
                className="whatsapp-input"
              />

              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowWhatsAppModal(false)}>
                  Cancel
                </button>
                <button 
                  className="btn-primary whatsapp"
                  onClick={() => handleBulkCommunication('whatsapp')}
                  disabled={!whatsAppMessage}
                >
                  <i className="fa-brands fa-whatsapp"></i> Send WhatsApp
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Email Modal */}
      {showEmailModal && (
        <motion.div 
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowEmailModal(false)}
        >
          <motion.div 
            className="modal email-modal"
            initial={{ scale: 0.9, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 50 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                <i className="fa-solid fa-envelope"></i>
                Send Email ({selectedBookings?.length || 0} recipients)
              </h2>
              <button className="close-btn" onClick={() => setShowEmailModal(false)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Subject</label>
                <input
                  type="text"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  placeholder="Email subject"
                />
              </div>

              <div className="form-group">
                <label>Message</label>
                <textarea
                  rows="6"
                  placeholder="Type your email message..."
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                />
              </div>

              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowEmailModal(false)}>
                  Cancel
                </button>
                <button 
                  className="btn-primary email"
                  onClick={() => handleBulkCommunication('email')}
                  disabled={!emailSubject || !emailBody}
                >
                  <i className="fa-solid fa-paper-plane"></i> Send Email
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Waitlist Modal */}
      {showWaitlistModal && (
        <motion.div 
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowWaitlistModal(false)}
        >
          <motion.div 
            className="modal waitlist-modal"
            initial={{ scale: 0.9, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 50 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                <i className="fa-solid fa-clock"></i>
                Waitlist Management
              </h2>
              <button className="close-btn" onClick={() => setShowWaitlistModal(false)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              {filteredBookings?.filter(b => b.status === 'waitlist')?.length > 0 ? (
                <div className="waitlist-items">
                  {filteredBookings
                    .filter(b => b.status === 'waitlist')
                    .map((booking, index) => (
                      <div key={booking.id} className="waitlist-item">
                        <div className="waitlist-position">{index + 1}</div>
                        <div className="waitlist-info">
                          <h4>{booking.member_full_name || booking.full_name}</h4>
                          <p>Booking: #{booking.booking_number}</p>
                          <p>Date: {booking.booking_date}</p>
                        </div>
                        <div className="waitlist-actions">
                          <button 
                            className="action-btn promote"
                            onClick={() => promoteFromWaitlist(booking.id)}
                          >
                            <i className="fa-solid fa-arrow-up"></i> Promote
                          </button>
                          <button 
                            className="action-btn remove"
                            onClick={() => removeFromWaitlist(booking.id)}
                          >
                            <i className="fa-solid fa-times"></i> Remove
                          </button>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <p className="no-data">No items in waitlist</p>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Feedback Modal */}
      {showFeedbackModal && (
        <motion.div 
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowFeedbackModal(false)}
        >
          <motion.div 
            className="modal feedback-modal"
            initial={{ scale: 0.9, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 50 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                <i className="fa-solid fa-star"></i>
                User Feedback
              </h2>
              <button className="close-btn" onClick={() => setShowFeedbackModal(false)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              {feedbacks?.length > 0 ? (
                <div className="feedback-list">
                  {feedbacks.map(feedback => (
                    <div key={feedback.id} className="feedback-item">
                      <div className="feedback-header">
                        <span className="feedback-user">{feedback.userName}</span>
                        <span className="feedback-rating">
                          {'⭐'.repeat(feedback.rating)}
                        </span>
                        <span className="feedback-time">
                          {new Date(feedback.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="feedback-comment">{feedback.comment}</p>
                      {!feedback.response ? (
                        <div className="feedback-response">
                          <textarea
                            placeholder="Write a response..."
                            rows="2"
                          />
                          <button 
                            className="btn-primary small"
                            onClick={() => respondToFeedback(feedback.id, 'Response')}
                          >
                            Respond
                          </button>
                        </div>
                      ) : (
                        <div className="response-display">
                          <strong>Response:</strong> {feedback.response}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-data">No feedback yet</p>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Maintenance Modal */}
      {showMaintenanceModal && (
        <motion.div 
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowMaintenanceModal(false)}
        >
          <motion.div 
            className="modal maintenance-modal"
            initial={{ scale: 0.9, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 50 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                <i className="fa-solid fa-tools"></i>
                Schedule Maintenance
              </h2>
              <button className="close-btn" onClick={() => setShowMaintenanceModal(false)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Facility</label>
                <select
                  value={maintenanceItem.facility}
                  onChange={(e) => setMaintenanceItem({...maintenanceItem, facility: e.target.value})}
                >
                  <option value="">Select facility</option>
                  {getAllLocationNames().map(f => (
                    <option key={f} value={f}>{f}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={maintenanceItem.startDate}
                    onChange={(e) => setMaintenanceItem({...maintenanceItem, startDate: e.target.value})}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={maintenanceItem.endDate}
                    onChange={(e) => setMaintenanceItem({...maintenanceItem, endDate: e.target.value})}
                    min={maintenanceItem.startDate}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Reason</label>
                <textarea
                  rows="3"
                  placeholder="Reason for maintenance..."
                  value={maintenanceItem.reason}
                  onChange={(e) => setMaintenanceItem({...maintenanceItem, reason: e.target.value})}
                />
              </div>

              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowMaintenanceModal(false)}>
                  Cancel
                </button>
                <button 
                  className="btn-primary"
                  onClick={scheduleMaintenance}
                  disabled={!maintenanceItem.facility || !maintenanceItem.startDate || !maintenanceItem.endDate}
                >
                  <i className="fa-solid fa-calendar-plus"></i> Schedule
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Audit Log Modal */}
      {showAuditLogModal && (
        <motion.div 
          className="modal-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setShowAuditLogModal(false)}
        >
          <motion.div 
            className="modal audit-modal"
            initial={{ scale: 0.9, y: 50 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 50 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>
                <i className="fa-solid fa-history"></i>
                Audit Log
              </h2>
              <button className="close-btn" onClick={() => setShowAuditLogModal(false)}>
                <i className="fa-solid fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="audit-filters">
                <input
                  type="text"
                  placeholder="Search audit log..."
                  className="audit-search"
                />
                <select className="audit-filter">
                  <option value="all">All Actions</option>
                  <option value="booking">Bookings</option>
                  <option value="membership">Memberships</option>
                  <option value="equipment">Equipment</option>
                  <option value="location">Location Changes</option>
                  <option value="communication">Communications</option>
                  <option value="admin">Admin Actions</option>
                </select>
              </div>

              <div className="audit-list">
                {auditLog?.length > 0 ? (
                  auditLog.slice(0, 50).map(entry => (
                    <div key={entry.id} className="audit-item">
                      <div className="audit-time">
                        {new Date(entry.timestamp).toLocaleString()}
                      </div>
                      <div className="audit-user">
                        <i className="fa-solid fa-user"></i>
                        {entry.userName}
                      </div>
                      <div className="audit-action">{entry.action}</div>
                    </div>
                  ))
                ) : (
                  <p className="no-data">No audit entries</p>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AdminModals;